// #3187 APPROVE.4 — the Client Portal approvals contract (APPROVE.3 recon §11,
// as merged in 3a #3831; 2b's Lead routes; G1/G2 rulings 6564562912,
// 6564588711). Types, paths and copy shared by the portal screens.

import { ApiError } from './api';

/** GET /client-approval-requests/pending?projectId= (3a). */
export interface PendingClientApproval {
  requestId: string;
  artefactId: string;
  artefactType: string;
  title: string;
  versionId: string;
  version: string;
  openedAt: string;
  approvedCount: number;
  approverCount: number;
  atRisk: boolean;
  canRespond: boolean;
}

/** GET /client-approval-requests/:id/version (3a). */
export interface ClientApprovalVersion {
  requestId: string;
  artefactId: string;
  artefactType: string;
  title: string;
  status: string;
  version: { id: string; version: string; notes: string | null };
  lastClientApproved: { id: string; version: string; notes: string | null } | null;
}

/** GET /client-approval-requests?projectId= (3a; readers narrowed in 3b). */
export interface ClientApprovalHistoryItem {
  requestId: string;
  artefactId: string;
  artefactType: string;
  title: string;
  versionId: string;
  version: string;
  mode: string;
  status: string;
  openedAt: string;
  closedAt: string | null;
  atRisk: boolean;
  approverCount: number;
  approvedCount: number;
  responses: Array<{
    stakeholderId: string;
    userId: string;
    name: string | null;
    decision: string;
    comment: string | null;
    respondedAt: string;
    channel: string;
  }>;
}

/** POST /artefacts/:id/client-approval — 3b's response (recon §11). */
export interface ClientApprovalDecisionResult {
  requestStatus: string;
  approvedCount: number;
  approverCount: number;
}

/** GET /client/projects/:id/approval-settings (G1, ruling 6564562912). */
export interface ClientApprovalSettings {
  mode: 'ALL' | 'ASSIGNED';
  assignments: Record<string, string[]>;
  approvers: Array<{ stakeholderId: string; name: string; isLead: boolean; confirmationPending: boolean }>;
  viewerIsLead: boolean;
  viewerCanConfirm: boolean;
  /** Codex R2-2: stored assignees the writer would now refuse (revoked, de-flagged). */
  ineligibleAssignees?: Array<{ stakeholderId: string; name: string }>;
}

export const CLIENT_APPROVALS_BASE = '/client/approvals';
/** G2: the generic landing (fixed path, no id). Must equal the API's CLIENT_APPROVAL_UNAVAILABLE_PATH. */
export const CLIENT_APPROVAL_UNAVAILABLE_PATH = '/client/approvals/unavailable';
export const clientApprovalVersionPath = (artefactId: string, requestId: string) =>
  `/client/approvals/${encodeURIComponent(artefactId)}/requests/${encodeURIComponent(requestId)}`;

export const NO_LONGER_AWAITING_COPY = 'This is no longer awaiting your decision.';
export const NO_BASELINE_COPY = 'No earlier client-approved version to compare.';
export const FORWARD_ONLY_COPY = 'Changes apply only to the next version submitted for approval. Requests already open keep the approvers they started with.';
export const AT_RISK_COPY = 'Proceeding at risk — awaiting your approval';
export const REASON_MIN = 10;

export const progressLabel = (approved: number, total: number) => `${approved} of ${total} approved`;

export function channelLabel(channel: string): string {
  if (channel === 'EMAIL_LINK') return 'Email link';
  if (channel === 'PORTAL') return 'Client Portal';
  return channel;
}

export function decisionLabel(decision: string): string {
  if (decision === 'APPROVE') return 'Approved';
  if (decision === 'REQUEST_CHANGES') return 'Requested changes';
  return decision;
}

/**
 * The decision route's errors (recon §11), each shown, never swallowed:
 * 404 generic, 403 not in the approver set, 409 answered / superseded /
 * handed off. 400 carries the server's own validation message.
 */
export function decisionErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'This approval request could not be found.';
    if (err.status === 403) return "You aren't an approver on this request.";
    if (err.status === 409) return `${NO_LONGER_AWAITING_COPY} It was already answered, or a newer version replaced it.`;
    if (err.status === 400 && err.message) return err.message;
  }
  return 'Something went wrong. Please try again or contact your BluePrint administrator.';
}

export const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};
